import axios from 'axios';
import { doc, setDoc, updateDoc, increment, collection, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080/api');

// Plan configurations (must match backend) - Premium Models Pricing
export const PLANS = {
  free: { 
    id: 'free',
    name: 'Free', 
    credits: 5, 
    price: 0, 
    priceAnnual: 0,
    pricePerCredit: null,
    discount: null
  },
  starter: { 
    id: 'starter',
    name: 'Starter', 
    credits: 30, 
    price: 599, 
    priceAnnual: 5999,
    pricePerCredit: 19.97,
    discount: null
  },
  basic: { 
    id: 'basic',
    name: 'Basic', 
    credits: 100, 
    price: 1499, 
    priceAnnual: 14999,
    pricePerCredit: 14.99,
    discount: '25% off'
  },
  pro: { 
    id: 'pro',
    name: 'Pro', 
    credits: 300, 
    price: 3499, 
    priceAnnual: 34999,
    pricePerCredit: 11.66,
    discount: '42% off'
  },
  hospital: { 
    id: 'hospital',
    name: 'Hospital', 
    credits: 1000, 
    price: 9999, 
    priceAnnual: 99999,
    pricePerCredit: 9.99,
    discount: '50% off'
  },
  enterprise: { 
    id: 'enterprise',
    name: 'Enterprise', 
    credits: 5000, 
    price: 39999, 
    priceAnnual: 399999,
    pricePerCredit: 8.00,
    discount: '60% off'
  }
};

// Generate unique invoice number
function generateInvoiceNumber() {
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `INV-${year}${month}-${random}`;
}

// Load Razorpay script dynamically
export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// Create order on backend
export async function createPaymentOrder(planId, billingCycle, userId, userEmail, organizationId) {
  const response = await axios.post(API_BASE_URL + '/payment/create-order', {
    planId,
    billingCycle,
    userId,
    userEmail,
    organizationId
  });
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to create order');
  }
  
  return response.data;
}

// Verify payment on backend
export async function verifyPayment(paymentData) {
  const response = await axios.post(API_BASE_URL + '/payment/verify', paymentData);
  
  if (!response.data.success) {
    throw new Error(response.data.error || 'Payment verification failed');
  }
  
  return response.data;
}

// Update organization credits after successful payment
export async function updateCreditsAfterPayment(organizationId, credits, planId, userId) {
  console.log('updateCreditsAfterPayment called:', { organizationId, credits, planId, userId });
  
  try {
    // Try to update organization if it exists
    if (organizationId) {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);
      if (orgSnap.exists()) {
        console.log('Updating organization credits');
        const currentCredits = orgSnap.data().creditsTotal || 0;
        await setDoc(orgRef, {
          plan: planId,
          creditsTotal: currentCredits + credits,
          updatedAt: serverTimestamp()
        }, { merge: true });
        console.log('Organization credits updated successfully');
        return;
      }
    }
    
    // Fallback: update user's credits directly
    if (userId) {
      console.log('Updating user credits for userId:', userId);
      const userRef = doc(db, 'users', userId);
      
      // Check if user document exists
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // User exists - add credits to existing
        const currentCredits = userSnap.data().creditsTotal || userSnap.data().credits || 0;
        console.log('User exists with credits:', currentCredits);
        await setDoc(userRef, {
          plan: planId,
          creditsTotal: currentCredits + credits,
          updatedAt: serverTimestamp()
        }, { merge: true });
      } else {
        // User doesn't exist - create with credits
        console.log('User does not exist, creating with credits:', credits);
        await setDoc(userRef, {
          plan: planId,
          creditsTotal: credits,
          creditsUsed: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      console.log('User credits updated successfully');
    }
  } catch (error) {
    console.error('Error updating credits:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error; // Re-throw to show the actual error
  }
}

// Helper function to remove undefined values (Firestore doesn't allow undefined)
function cleanUndefined(obj) {
  if (!obj || typeof obj !== 'object') {
    return {};
  }
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// Record payment in Firestore
export async function recordPayment(paymentData) {
  const paymentsRef = collection(db, 'payments');
  const paymentId = doc(paymentsRef).id;
  const paymentRef = doc(db, 'payments', paymentId);
  
  // Clean undefined values
  const cleanedData = cleanUndefined(paymentData);
  
  await setDoc(paymentRef, {
    id: paymentId,
    ...cleanedData,
    createdAt: serverTimestamp()
  });
  
  return paymentId;
}

// Generate and store invoice in Firestore
export async function generateInvoice(invoiceData) {
  const invoicesRef = collection(db, 'invoices');
  const invoiceId = doc(invoicesRef).id;
  const invoiceRef = doc(db, 'invoices', invoiceId);
  
  const invoiceNumber = generateInvoiceNumber();
  
  // Clean undefined values
  const cleanedData = cleanUndefined(invoiceData);
  
  const invoice = {
    id: invoiceId,
    invoiceNumber: invoiceNumber,
    ...cleanedData,
    status: 'paid',
    createdAt: serverTimestamp(),
    dueDate: serverTimestamp(), // Paid immediately
    paidAt: serverTimestamp()
  };
  
  await setDoc(invoiceRef, invoice);
  
  return { invoiceId, invoiceNumber };
}

// Get user details for invoice
async function getUserDetails(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      return userSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching user details:', error);
    return null;
  }
}

// Main function to initiate Razorpay payment
export async function initiatePayment({
  planId,
  billingCycle,
  userId,
  userEmail,
  userName,
  organizationId,
  organizationName,
  phone,
  onSuccess,
  onError,
  onDismiss
}) {
  try {
    // Load Razorpay script
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      throw new Error('Failed to load payment gateway. Please try again.');
    }
    
    // Create order
    const orderData = await createPaymentOrder(planId, billingCycle, userId, userEmail, organizationId);
    
    // Configure Razorpay options
    const options = {
      key: orderData.key,
      amount: orderData.order.amount,
      currency: orderData.order.currency,
      name: 'LatDiagnostics',
      description: orderData.plan.name + ' Plan - ' + orderData.plan.credits + ' Credits',
      order_id: orderData.order.id,
      prefill: {
        name: userName,
        email: userEmail,
        contact: phone || ''
      },
      notes: {
        planId: planId,
        organizationId: organizationId
      },
      theme: {
        color: '#0ea5e9'
      },
      handler: async function(response) {
        try {
          // Verify payment
          const verificationData = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            planId: planId,
            billingCycle: billingCycle,
            userId: userId,
            organizationId: organizationId
          });
          
          // Update credits in Firestore
          await updateCreditsAfterPayment(organizationId, orderData.plan.credits, planId, userId);
          
          // Get user details for invoice
          const userDetails = await getUserDetails(userId);
          
          // Calculate amount
          const amount = billingCycle === 'annual' 
            ? (orderData.plan.priceAnnual || Math.round(orderData.plan.price * 12 * 0.85))
            : orderData.plan.price;
          
          // Record payment
          const paymentRecordId = await recordPayment({
            razorpayOrderId: response.razorpay_order_id || '',
            razorpayPaymentId: response.razorpay_payment_id || '',
            userId: userId || '',
            userEmail: userEmail || '',
            userName: userName || '',
            userPhone: phone || '',
            organizationId: organizationId || '',
            organizationName: organizationName || '',
            planId: planId || '',
            planName: orderData.plan.name || '',
            credits: orderData.plan.credits || 0,
            amount: amount || 0,
            currency: 'INR',
            billingCycle: billingCycle || 'monthly',
            status: 'success'
          });
          
          // Generate Invoice
          const invoiceResult = await generateInvoice({
            userId: userId || '',
            userEmail: userEmail || '',
            organizationId: organizationId || '',
            paymentRecordId: paymentRecordId || '',
            paymentId: response.razorpay_payment_id || '',
            orderId: response.razorpay_order_id || '',
            planId: planId || '',
            planName: orderData.plan.name || '',
            credits: orderData.plan.credits || 0,
            amount: amount || 0,
            currency: 'INR',
            billingCycle: billingCycle || 'monthly',
            paymentMethod: 'Razorpay',
            // Customer details
            customerName: userName || userDetails?.name || 'Customer',
            customerEmail: userEmail || userDetails?.email || '',
            customerPhone: phone || userDetails?.phoneNumber || '',
            customerOrg: organizationName || userDetails?.organization || '',
            // Billing details
            billingAddress: userDetails?.address || '',
            gstin: userDetails?.gstin || ''
          });
          
          console.log('Invoice generated:', invoiceResult?.invoiceNumber);
          
          // Call success callback
          if (onSuccess) {
            onSuccess({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              plan: orderData.plan,
              credits: orderData.plan.credits,
              invoiceNumber: invoiceResult?.invoiceNumber || '',
              invoiceId: invoiceResult?.invoiceId || ''
            });
          }
        } catch (error) {
          console.error('Payment processing error:', error);
          if (onError) {
            onError('Payment was received but failed to update your account. Please contact support.');
          }
        }
      },
      modal: {
        ondismiss: function() {
          if (onDismiss) {
            onDismiss();
          }
        }
      }
    };
    
    // Open Razorpay checkout
    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', function(response) {
      console.error('Payment failed:', response.error);
      if (onError) {
        onError(response.error.description || 'Payment failed. Please try again.');
      }
    });
    razorpay.open();
    
  } catch (error) {
    console.error('Payment initiation error:', error);
    if (onError) {
      onError(error.message || 'Failed to initiate payment. Please try again.');
    }
  }
}

// Format price for display
export function formatPrice(price) {
  return new Intl.NumberFormat('en-IN').format(price);
}

// Get annual price (15% discount)
export function getAnnualPrice(monthlyPrice) {
  if (monthlyPrice === 0) return 0;
  return Math.round(monthlyPrice * 12 * 0.85);
}
